import type { ChangeEvent, FC } from 'react';
import React, { useEffect, useState } from 'react';
import {
  Mobile,
  Trans,
  useBrand,
  useTracking,
  useTranslation,
} from '@edreamsodigeo/frontend-core';
import {
  BackgroundImage,
  Box,
  Checkbox,
  CheckboxField,
  CheckboxLabel,
  Flex,
  Heading,
} from '@edreamsodigeo/prisma-design-system';

import BrandLogo from '@/components/FlightSummary/HotelVoucher/BrandLogo';
import PillsSection from '@/components/FlightSummary/HotelVoucher/HotelVoucherCard/PillsSection';
import useBrandColor from '@/clientState/hooks/useBrandColor';
import usePrimeSiteProperties from '@/queries/useprimeSiteProperties';
import useItineraryFromUrl from '@/clientState/hooks/useItineraryFromUrl';
import type { HotelVoucherCardProps } from '@/components/FlightSummary/HotelVoucher/HotelVoucherCard/config/types';

const HOTEL_ROOM_IMG_URL = '/images/tests/flight_summary/flight_summary@2x.png';

interface HighlightTextProps {
  textColor: string;
}

const HighlightText: FC<HighlightTextProps> = ({ textColor, children }) => {
  return (
    <Heading
      color={textColor}
      weight="medium"
      type="3"
      sx={{
        display: 'inline',
      }}
    >
      {children}
    </Heading>
  );
};

const HotelVoucherCard: FC<HotelVoucherCardProps> = ({
  amountNights = 0,
  destinationName = '',
  roundTrip,
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const { segmentKeys } = useItineraryFromUrl();

  const isRoundTrip = segmentKeys?.length === 2;
  const isRoundTripType = roundTrip ?? isRoundTrip;

  const { data: primeSitesResponse } = usePrimeSiteProperties();
  const isPrimeSite =
    primeSitesResponse?.primeSiteProperties?.activePrimeMarket?.flights ||
    false;

  const brand = useBrand();
  const { backgroundColor, textColor } = useBrandColor({ brand, isPrimeSite });
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

  useEffect(() => {
    trackEvent({
      category: 'checkout_flight_summary_results',
      action: 'prime_hotel-banner',
      label: 'prime_hotel-banner_onload_pag:flsum_RD:1',
    });
  }, [trackEvent]);

  const onCheckboxClick = (e: ChangeEvent<HTMLInputElement>): void => {
    const isChecked = e.target.checked;
    setIsCheckboxChecked(isChecked);
  };

  return (
    <Mobile>
      <Box py="base">
        <Box
          backgroundColor={backgroundColor}
          mt={amountNights && destinationName ? 'small' : 'big'}
        >
          <PillsSection
            amountNights={amountNights}
            destinationName={destinationName}
          />
          <Flex overflowX="hidden" justifyContent="space-between">
            <Flex flexDirection="column" minWidth="70%" my="base" ml="base">
              <Flex mt="tiny" sx={{ marginRight: 'auto' }}>
                <BrandLogo
                  width="auto"
                  height="16px"
                  type={isPrimeSite ? 'primeLong' : 'short'}
                  theme="negative"
                />
              </Flex>
              <Box mt="base">
                <Heading color="neutral.0" type="3" weight="medium">
                  <Trans
                    i18nKey={
                      isRoundTripType
                        ? 'voucher.one.way.title.text'
                        : 'voucher.round.trip.title.text'
                    }
                    values={{
                      highlight: t('voucher.hotel.text'),
                    }}
                    components={{
                      highlight: <HighlightText textColor={textColor} />,
                    }}
                  />
                </Heading>
              </Box>
              <CheckboxField mt="base">
                <Checkbox
                  checked={isCheckboxChecked}
                  onChange={onCheckboxClick}
                  sx={{
                    backgroundColor: 'transparent',
                    borderColor: 'neutral.0',
                    color: 'neutral.0',
                    height: '20px',
                    width: '20px',
                  }}
                  hasError // workaround to remove :hover and :focus styles
                />
                <CheckboxLabel
                  sx={{
                    color: 'neutral.0',
                    fontSize: '12px',
                    lineHeight: '20px',
                  }}
                >
                  {t('voucher.checkbox.text')}
                </CheckboxLabel>
              </CheckboxField>
            </Flex>
            <Flex flexBasis="100px" justifyContent="flex-end" flexShrink={0}>
              <BackgroundImage
                backgroundImage={`url("${HOTEL_ROOM_IMG_URL}")`}
                sx={{
                  backgroundPosition: 'bottom right',
                }}
                alt="Image of a hotel bed"
              />
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Mobile>
  );
};

export default HotelVoucherCard;
